import { useCallback, useContext, useEffect, useState } from 'react';
import { getLessonResults, getThemeResults, LessonResults } from '../../../api/results';
import useThrowAsyncError from '../../../../../../hooks/useThrowAsyncError';
import { useResultsDataContext } from '../../../context/ResultsDataContext';
import { useFilters } from '../../../context/filter-context/FilterContext';
import { Result } from '../../../../../../utils/results';
import ModuleContext from '../../../../_contexts/ModuleContext';
import useLicenses from '../../../../../../hooks/useLicenses';

export type ResultWithGroup = Result & { groupId: string };

export function mapLessonResults(lessonResults: LessonResults['scores'], groupId: string): ResultWithGroup[] {
  const scores: ResultWithGroup[] = [];

  Object.entries(lessonResults).forEach(([nodeId, members]) => {
    Object.entries(members).forEach(([memberId, scoresOfStudent]) => {
      scoresOfStudent.forEach(score => {
        scores.push({
          ...score,
          memberId,
          nodeId,
          groupId,
        });
      });
    });
  });

  return scores;
}

const apiRequestQueryString = new URLSearchParams({
  includeFreeExercises: 'true',
  includeCompletedAssignments: 'true',
  includeUncompletedAssignments: 'true',
});

export default function useResults() {
  const { selectedModule, selectedGroups } = useFilters();
  const { groups } = useResultsDataContext();
  const [results, setResults] = useState<ResultWithGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const handleError = useThrowAsyncError();
  const { module } = useContext(ModuleContext);
  const { isModuleLicensed } = useLicenses();

  const isUnlicensed = !isModuleLicensed(selectedModule);

  const fetchData = useCallback(async () => {
    if (!selectedModule || !selectedGroups.length || isUnlicensed) return;

    setLoading(true);
    (selectedModule.isVirtualExerciseModule
      ? getThemeResults(module!.id, selectedGroups[0].id, apiRequestQueryString)
      : getLessonResults(selectedModule.id, selectedGroups[0].id, apiRequestQueryString)
    )
      .then(resultsResponse => {
        setResults(mapLessonResults(resultsResponse.scores, selectedGroups[0].id));
      })
      .catch(handleError)
      .finally(() => {
        setLoading(false);
      });
  }, [selectedModule, selectedGroups, isUnlicensed, module, handleError]);

  useEffect(() => {
    if (!selectedModule || !selectedGroups.length) return;

    fetchData();
  }, [groups, handleError, selectedGroups, selectedModule, fetchData]);

  return {
    results,
    loading,
    fetchData,
  };
}
